'use client';

import * as React from 'react';
import { ArrowUpRight, Package, ShoppingCart } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useToast } from '@/components/ui/use-toast';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

import { PageHeader } from '@/components/ui/page-header';
import { TableCard } from '@/components/ui/table-card';
import { useOrdersList, useRefundOrder, useSyncOrderToProvider, useDeleteOrders } from '@/lib/api/orders';
import { formatMoney } from "@/lib/money";
import { OrdersTable } from './components/OrdersTable';
import { ExportOrdersDialog } from './components/export-orders-dialog';
import { useTablePage } from '@/hooks/use-table-page';
import { ordersFilterMapping, ordersQueryParamsMapping, ordersSortMapping } from './utils/table-mappings';
import { Order } from '@tagadapay/crm-entities';

import { withRequiredPermission } from '@/components/auth/with-required-permission';
import { PERMISSIONS } from '@/hooks/use-perm';

export default withRequiredPermission(OrdersPage, PERMISSIONS.ORDERS_VIEW);

function OrdersPage() {
  const { t } = useTranslation(['orders', 'common']);
  const { toast } = useToast();

  // Use generic table page hook
  const { searchQuery, setSearchQuery, tableState, setTableState, queryInput } = useTablePage({
    initialSorting: [{ id: 'createdAt', desc: true }],
    filterMapping: ordersFilterMapping,
    sortMapping: ordersSortMapping,
    queryParamsMapping: ordersQueryParamsMapping,
  });

  // Use tRPC hook to fetch orders
  const {
    data: ordersResponse,
    isLoading: loading,
    isFetching: fetching,
    error,
    refetch,
  } = useOrdersList(queryInput);

  const orders = (ordersResponse?.orders || []) as Order.Order[];
  const total = ordersResponse?.total || 0;
  const pageCount = ordersResponse?.pageCount || 0;

  // Extract options - ensure object reference changes when data changes
  const options = React.useMemo(() => {
    const backendOptions = (ordersResponse as any)?.options;
    if (!backendOptions) {
      return {
        'orders.status': [],
        'stores.id': [],
      };
    }
    return {
      'orders.status': backendOptions['orders.status'] || [],
      'stores.id': backendOptions['stores.id'] || [],
    };
  }, [ordersResponse]);

  const refundOrderMutation = useRefundOrder();
  const syncOrderMutation = useSyncOrderToProvider();
  const deleteOrdersMutation = useDeleteOrders();
  const [orderToRefund, setOrderToRefund] = React.useState<string | null>(null);
  const [refundRequestId, setRefundRequestId] = React.useState<string | null>(null);

  const handleRefundOrder = React.useCallback((orderId: string) => {
    setRefundRequestId(crypto.randomUUID());
    setOrderToRefund(orderId);
  }, []);

  const confirmRefundOrder = React.useCallback(() => {
    if (!orderToRefund || refundRequestId === null) return;

    refundOrderMutation.mutate(
      {
        orderId: orderToRefund,
        requestId: refundRequestId,
        refundType: 'full',
      },
      {
        onSuccess: () => {
          toast({ description: t('orders:refundSuccess') });
          setOrderToRefund(null);
        },
        onError: (error) => {
          toast({
            description: t('orders:refundError', { message: error.message }),
            variant: 'destructive',
          });
          setOrderToRefund(null);
        },
      },
    );
  }, [orderToRefund, refundOrderMutation, refundRequestId, t, toast]);

  const handleSyncOrder = React.useCallback(
    (orderId: string) => {
      syncOrderMutation.mutate(
        {
          orderId,
        },
        {
          onSuccess: () => {
            toast({ description: t('orders:syncSuccess') });
          },
          onError: (error) => {
            toast({ description: t('orders:syncError', { message: error.message }), variant: 'destructive' });
          },
        },
      );
    },
    [syncOrderMutation, t, toast],
  );

  const handleDeleteOrders = React.useCallback(
    (orderIds: string[]) => {
      deleteOrdersMutation.mutate({ orderIds });
    },
    [deleteOrdersMutation],
  );

  const [exportOpen, setExportOpen] = React.useState(false);
  const handleExportOrders = React.useCallback(() => setExportOpen(true), []);

  const orderToRefundData = React.useMemo(() => {
    return orders.find((o) => o.id === orderToRefund);
  }, [orders, orderToRefund]);

  return (
    <div className="flex flex-col space-y-4 sm:space-y-6">
      {/* Page header */}
      <PageHeader title={t('orders:title')} description={t('orders:description')} />

      {/* Stats cards */}
      {/* <ResourceStatsCardGrid cards={statsCards} loading={loading} /> */}

      {/* Orders table */}
      <TableCard>
        <OrdersTable
          orders={orders}
          total={total}
          pageCount={pageCount}
          options={options}
          loading={loading || fetching}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          onTableStateChange={setTableState}
          onRefundOrder={handleRefundOrder}
          onSyncOrder={handleSyncOrder}
          onDeleteOrders={handleDeleteOrders}
          onExportOrders={handleExportOrders}
          isRefunding={refundOrderMutation.isPending}
          isSyncing={syncOrderMutation.isPending}
          isDeleting={deleteOrdersMutation.isPending}
          onRefetch={refetch}
        />

        {/* Error message */}
        {error && (
          <div className="mt-4 rounded border border-red-400 bg-red-100 px-4 py-3 text-red-700">
            {error instanceof Error ? error.message : t('orders:loadError')}
          </div>
        )}
      </TableCard>

      <ExportOrdersDialog open={exportOpen} onOpenChange={setExportOpen} />

      <AlertDialog open={!!orderToRefund} onOpenChange={(open) => !open && setOrderToRefund(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('orders:refundOrderDialog')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('orders:refundOrderConfirm', {
                amount: orderToRefundData
                  ? formatMoney(
                      orderToRefundData.summaries?.[0]?.totalAdjustedAmount || 0,
                      orderToRefundData.currency || 'USD',
                    )
                  : '',
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('orders:cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmRefundOrder}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t('orders:refundOrder')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
